import type { ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { ManualMeasurementFormSchema, ManualMeasurementFormValues, PARAMETER_OPTIONS } from '../../../types/measurement';
import { useSubmitMeasurement } from '../../../hooks/useSubmitMeasurement';
import type { MeasurementPayload, MeasurementResponse } from '../../../services/measurementService';
import { Button } from '../../atoms/Button/Button';
import { Input } from '../../atoms/Input/Input';
import { Select } from '../../atoms/Select/Select';
import { useTranslation } from '../../../i18n/useTranslation';
import { parameterLabel } from '../../measurements/measurementHistoryUtils';
import { toLocalDateTimeInput } from '../TestSessionForm/testSessionFormSchema';
import './ManualMeasurementForm.css';
import { Alert } from '../../atoms/Alert/Alert';

export interface ManualMeasurementFormProps {
  aquariumId: string;
  onSuccess?: (id: string) => void;
  /**
   * Optional replacement for the default save call (POST /measurements) —
   * e.g. the journal "Record new" tab records and links in one request.
   */
  submit?: (payload: MeasurementPayload) => Promise<MeasurementResponse>;
}

/** Now as a `datetime-local` value in the user's time zone (not UTC — audit §3.4). */
function getDefaultMeasuredAt(): string {
  return toLocalDateTimeInput(new Date());
}

export function ManualMeasurementForm({ aquariumId, onSuccess, submit }: ManualMeasurementFormProps): ReactElement {
  const { t } = useTranslation();
  // Schema messages are translation keys (types/measurement.ts).
  const fieldError = (key: string | undefined): string | undefined => (key ? t(key) : undefined);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ManualMeasurementFormValues>({
    resolver: zodResolver(ManualMeasurementFormSchema),
    defaultValues: {
      parameterId: '',
      value: NaN,
      measuredAt: getDefaultMeasuredAt(),
    },
  });

  const mutation = useSubmitMeasurement({ submit });

  const onSubmit = (data: ManualMeasurementFormValues): void => {
    setSuccessMessage(null);
    const selectedOption = PARAMETER_OPTIONS.find((p) => p.id === data.parameterId);

    mutation.mutate(
      {
        aquariumId,
        parameterId: data.parameterId,
        parameterName: selectedOption?.name ?? data.parameterId,
        value: data.value,
        unit: selectedOption?.unit ?? '',
        // The input is local wall time without a zone; send an absolute instant.
        measuredAt: new Date(data.measuredAt).toISOString(),
      },
      {
        onSuccess: (response) => {
          setSuccessMessage(`${selectedOption?.name ?? data.parameterId} logged successfully.`);
          reset({
            parameterId: '',
            value: NaN,
            measuredAt: getDefaultMeasuredAt(),
          });
          onSuccess?.(response.id);
        },
      },
    );
  };

  const mutationError = mutation.isError ? t('measurement.form.submitError') : null;

  return (
    <form
      className="manual-measurement-form"
      onSubmit={handleSubmit(onSubmit)}
      aria-label={t('measurement.form.ariaLabel')}
      noValidate
    >
      <div className="manual-measurement-form__field">
        <Select
          id="parameterId"
          label={t('measurement.form.parameter')}
          className={`manual-measurement-form__select${errors.parameterId ? ' manual-measurement-form__select--error' : ''}`}
          aria-invalid={Boolean(errors.parameterId)}
          aria-describedby={errors.parameterId ? 'parameterId-error' : undefined}
          error={fieldError(errors.parameterId?.message)}
          {...register('parameterId')}
        >
          <option value="">{t('measurement.form.selectParameter')}</option>
          {PARAMETER_OPTIONS.map((option) => (
            <option key={option.id} value={option.id}>
              {option.unit
                ? `${parameterLabel(t, option.id, option.name)} (${option.unit})`
                : parameterLabel(t, option.id, option.name)}
            </option>
          ))}
        </Select>
      </div>

      <div className="manual-measurement-form__field">
        <Input
          id="value"
          type="number"
          inputMode="decimal"
          step="any"
          label={t('measurement.form.value')}
          className={`manual-measurement-form__input${errors.value ? ' manual-measurement-form__input--error' : ''}`}
          aria-invalid={Boolean(errors.value)}
          aria-describedby={errors.value ? 'value-error' : undefined}
          placeholder={t('measurement.form.valuePlaceholder')}
          error={fieldError(errors.value?.message)}
          {...register('value', {
            setValueAs: (v: string) => (v === '' ? NaN : parseFloat(v)),
          })}
        />
      </div>

      <div className="manual-measurement-form__field">
        <Input
          id="measuredAt"
          type="datetime-local"
          label={t('measurement.form.measuredAt')}
          className={`manual-measurement-form__input${errors.measuredAt ? ' manual-measurement-form__input--error' : ''}`}
          aria-invalid={Boolean(errors.measuredAt)}
          aria-describedby={errors.measuredAt ? 'measuredAt-error' : undefined}
          error={fieldError(errors.measuredAt?.message)}
          {...register('measuredAt')}
        />
      </div>

      {mutationError && <Alert tone="danger">{mutationError}</Alert>}

      {successMessage && <Alert tone="success">{successMessage}</Alert>}

      <div className="manual-measurement-form__actions">
        <Button
          type="submit"
          isLoading={isSubmitting || mutation.isPending}
          disabled={isSubmitting || mutation.isPending}
        >
          {(isSubmitting || mutation.isPending) ? 'Logging...' : t('measurement.form.submit')}
        </Button>
      </div>
    </form>
  );
}
